'use client'

import { useState } from 'react'
import { Modal } from '../ui/modal'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import Image from 'next/image'
import { useSubscribeForNewsletterMutation } from 'graphql/generated/graphql'

interface ExitIntentModalProps {
  isOpen: boolean
  onClose: () => void
}

const ExitIntentModal = ({ isOpen, onClose }: ExitIntentModalProps) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')
  const [newsletterSub, { loading }] = useSubscribeForNewsletterMutation({fetchPolicy: 'no-cache'})

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('Please fill in all fields')
      setIsSubmitting(false)
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address')
      setIsSubmitting(false)
      return
    }

    try {
      // const res = await newsletterSub({
      //   variables: {
      //     input: {
      //       first_name: formData.firstName,
      //       last_name: formData.lastName,
      //       email: formData.email
      //     }
      //   }
      // })
      // console.log('Subscription response:', res)
      // if (res.data?.addSubscriber?.success) {
      //   setIsSuccess(true)
      //   setTimeout(() => {
      //     onClose()
      //     setIsSuccess(false)
      //     setFormData({ firstName: '', lastName: '', email: '' })
      //   }, 2000)
      // }
      await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email
        }),
      });

      setIsSuccess(true)
      setTimeout(() => {
        onClose()
        setIsSuccess(false)
        setFormData({ firstName: '', lastName: '', email: '' })
      }, 2000)
    } catch (err) {
      console.error('Error subscribing:', err)
      setIsSuccess(true)
      setTimeout(() => {
        onClose()
        setIsSuccess(false)
        setFormData({ firstName: '', lastName: '', email: '' })
      }, 2000)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <div className="flex xs:flex-col-reverse p-1">
        {/* Left Side - Form */}
        <div className="w-1/2 xs:w-full flex flex-col justify-center px-6 py-8">
          {/* Logo */}
          <div className="pb-4">
            <img
              src="/assets/logo.svg"
              alt="Culineer"
              className="h-[45px] w-[134px]"
            />
          </div>

          {!isSuccess ? (
            <>
              {/* Title */}
              <h2 className="text-2xl font-bold mb-3">
                Before you go...
              </h2>

              <p className="text-gray-700 mb-4 text-sm">
                Sign up for our <span className="font-bold">newsletter</span> to discover where your food comes from and
                get inspired with seasonal recipes!
              </p>

              {/* Features */}
              <ul className="space-y-2 text-gray-700 mb-5">
                <li className="flex items-center text-sm">
                  <CheckCircleIcon
                    className="text-blue-500 mr-2 h-4 flex-shrink-0"
                    fill="rgb(63, 131, 248)"
                  />
                  Learn about local food sources near you
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircleIcon
                    className="text-blue-500 mr-2 h-4 flex-shrink-0"
                    fill="rgb(63, 131, 248)"
                  />
                  Get seasonal recipe inspiration
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircleIcon
                    className="text-blue-500 mr-2 h-4 flex-shrink-0"
                    fill="rgb(63, 131, 248)"
                  />
                  Read about local farmers
                </li>
              </ul>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    required
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    required
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  required
                />

                {error && (
                  <p className="text-red-500 text-xs">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-white px-6 py-2 rounded-full font-normal shadow hover:bg-primary/80 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Subscribing...' : 'Sign Me Up'}
                </button>
              </form>

              <button
                onClick={onClose}
                className="mt-3 text-gray-500 text-xs hover:text-gray-700 transition text-center w-full"
              >
                No thanks, I'll explore on my own
              </button>
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircleIcon
                className="mx-auto h-16 w-16 mb-4"
                fill="rgb(133, 195, 73)"
              />
              <h3 className="text-2xl font-bold mb-2 text-primary">
                Welcome to Culineer!
              </h3>
              <p className="text-gray-700">
                Check your email for delicious updates.
              </p>
            </div>
          )}
        </div>

        {/* Right Side - Image */}
        <div className="w-1/2 xs:w-full relative">
          <img
            src="/images/exit-modal-farm-stand.png"
            alt="Fresh Local Produce"
            className="rounded-r-2xl h-full object-cover"
          />
        </div>
      </div>
    </Modal>
  )
}

export default ExitIntentModal
